// Global Instructions Rule Applied!

import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { AppDispatch } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import {
  setFundTransactions,
  appendFundTransactions,
  setFundTransactionsActiveFilters,
  setFundTransactionsActiveSorting
} from '../../../state_manager/state_update_actions/fund_transactions/index.ts'
import {
  fetchFundTransactionsFiltered,
  fetchFundTransactionsFilterOptions
} from '../services/fundTransactionsService.ts'
import { useMenuItemUserPreference } from '../../global_configs/useMenuItemUserPreference.ts'
import {
  mergeUserPreferenceRowData,
  removeUserPreferenceForMenuItem
} from '../../../state_manager/state_update_actions/global_configs/actions.ts'
import {
  areFilterValuesEqual,
  areSortConfigsEqual,
  FilterValue,
  SortConfig,
  PREF_TABLE_COLUMN_KEYS,
  PREF_TABLE_FILTERS,
  PREF_TABLE_SORTING,
  resolveTableFiltersFromPreferenceRow,
  resolveTableSortingFromPreferenceRow
} from '../../global_configs/tableViewPreferences.ts'
import { useReportingDefaultSorting } from '../../global_configs/useReportingDefaultSorting.ts'
import { MENU_ITEM_KEY_FUND_TRANSACTIONS } from '../../../state_manager/globals.ts'
import { fetchGlobalConfigsThunk } from '../../global_configs/controller.ts'
import { useFundTransactionsReportId } from '../../../state_manager/hooks/useFundTransactionsReportId.ts'

const DEFAULT_FUND_TRANSACTIONS_FILTERS: FilterValue[] = []

export const useFundTransactionsData = () => {
  const dispatch = useDispatch<AppDispatch>()

  const { menuItemId, preferenceRow, persistPreference, deletePreference } = useMenuItemUserPreference(
    MENU_ITEM_KEY_FUND_TRANSACTIONS
  )

  const reportId = useFundTransactionsReportId()
  const defaultTableSorting = useReportingDefaultSorting(reportId)

  const [dataLoading, setDataLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [prefsReady, setPrefsReady] = useState(false)
  const [filterOptions, setFilterOptions] = useState<Record<string, Array<{ value: string; label: string }>>>({})
  const [filterOptionsLoading, setFilterOptionsLoading] = useState(false)
  const skipPersistRef = useRef(true)

  const data = useSelector((state: any) => state.fundTransactions?.data ?? [])
  const activeFilters = useSelector((state: any) => state.fundTransactions?.activeFilters ?? [])
  const activeSorting = useSelector((state: any) => state.fundTransactions?.activeSorting ?? [])
  const hasMore = useSelector((state: any) => state.fundTransactions?.hasMore ?? false)
  const currentPage = useSelector((state: any) => state.fundTransactions?.currentPage ?? 1)
  const menuItems = useSelector((state: any) => state.globalConfigs?.menu_items ?? [])

  // Resolve user preferences once globalConfigs has loaded.
  useEffect(() => {
    if (menuItems.length === 0) return
    if (!menuItemId) {
      dispatch(setFundTransactionsActiveFilters({ filters: DEFAULT_FUND_TRANSACTIONS_FILTERS }))
      dispatch(setFundTransactionsActiveSorting({ sorting: defaultTableSorting }))
      setPrefsReady(true)
      return
    }
    const filters = resolveTableFiltersFromPreferenceRow(preferenceRow, DEFAULT_FUND_TRANSACTIONS_FILTERS)
    const sorting = resolveTableSortingFromPreferenceRow(preferenceRow, defaultTableSorting)
    if (!areFilterValuesEqual(filters, activeFilters)) {
      dispatch(setFundTransactionsActiveFilters({ filters }))
    }
    if (!areSortConfigsEqual(sorting, activeSorting)) {
      dispatch(setFundTransactionsActiveSorting({ sorting }))
    }
    setPrefsReady(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuItemId, menuItems.length, preferenceRow, defaultTableSorting, dispatch])

  const handleFetch = useCallback(
    async (sorting: SortConfig[] = [], filters?: FilterValue[]) => {
      try {
        setDataLoading(true)
        const token = getAccessToken(store.getState().auth)
        const response = await fetchFundTransactionsFiltered(sorting, filters ?? [], 1, null, token)
        dispatch(setFundTransactions({ response }))
      } catch (e) {
        if (process.env.NODE_ENV === 'development') console.error('Error fetching fund transactions:', e)
      } finally {
        setDataLoading(false)
      }
    },
    [dispatch]
  )

  const handleSetFilters = useCallback(
    (filters: FilterValue[]) => {
      if (areFilterValuesEqual(filters, activeFilters)) return
      dispatch(setFundTransactionsActiveFilters({ filters }))
    },
    [dispatch, activeFilters]
  )

  const handleSetSorting = useCallback(
    (sorting: SortConfig[]) => {
      if (areSortConfigsEqual(sorting, activeSorting)) return
      dispatch(setFundTransactionsActiveSorting({ sorting }))
    },
    [dispatch, activeSorting]
  )

  const handleResetFilters = useCallback(() => {
    if (!menuItemId) {
      dispatch(setFundTransactionsActiveFilters({ filters: DEFAULT_FUND_TRANSACTIONS_FILTERS }))
      return
    }
    void (async () => {
      const result = await deletePreference()
      if (!result.success) return
      dispatch(removeUserPreferenceForMenuItem(menuItemId))
      skipPersistRef.current = true
      dispatch(setFundTransactionsActiveFilters({ filters: DEFAULT_FUND_TRANSACTIONS_FILTERS }))
      dispatch(setFundTransactionsActiveSorting({ sorting: defaultTableSorting }))
    })()
  }, [dispatch, menuItemId, deletePreference, defaultTableSorting])

  const persistMergedPreference = useCallback(
    async (extra: Record<string, unknown> = {}) => {
      if (!menuItemId) return { success: false as const, error: 'No menu item' }
      const reports = store.getState().globalConfigs?.user_preferences_reports ?? []
      const hadExistingRow = reports.some((p: any) => p.menu_item_id === menuItemId)
      const row = reports.find((p: any) => p.menu_item_id === menuItemId)
      const merged: Record<string, unknown> = {
        ...(row?.data ?? {}),
        ...extra,
        [PREF_TABLE_FILTERS]: activeFilters,
        [PREF_TABLE_SORTING]: activeSorting
      }
      const result = await persistPreference(undefined, merged)
      if (result.success) {
        if (hadExistingRow) {
          dispatch(mergeUserPreferenceRowData(menuItemId, merged))
        } else {
          const user = store.getState().auth.user
          if (user) {
            await fetchGlobalConfigsThunk(user)(dispatch)
          }
        }
      }
      return result
    },
    [menuItemId, activeFilters, activeSorting, persistPreference, dispatch]
  )

  const persistColumnFieldOrder = useCallback(
    async (fieldNames: string[]) => persistMergedPreference({ [PREF_TABLE_COLUMN_KEYS]: fieldNames }),
    [persistMergedPreference]
  )

  const resetColumnLayoutToDefault = useCallback(async () => {
    if (!menuItemId) return { success: false as const, error: 'No menu item' }
    const reports = store.getState().globalConfigs?.user_preferences_reports ?? []
    const hadExistingRow = reports.some((p: any) => p.menu_item_id === menuItemId)
    const row = reports.find((p: any) => p.menu_item_id === menuItemId)
    const base: Record<string, unknown> = { ...(row?.data ?? {}) }
    delete base[PREF_TABLE_COLUMN_KEYS]
    const merged: Record<string, unknown> = {
      ...base,
      [PREF_TABLE_FILTERS]: activeFilters,
      [PREF_TABLE_SORTING]: activeSorting
    }
    const result = await persistPreference(undefined, merged)
    if (result.success) {
      if (hadExistingRow) {
        dispatch(mergeUserPreferenceRowData(menuItemId, merged))
      } else {
        const user = store.getState().auth.user
        if (user) {
          await fetchGlobalConfigsThunk(user)(dispatch)
        }
      }
    }
    return result
  }, [menuItemId, activeFilters, activeSorting, persistPreference, dispatch])

  const handleLoadMore = useCallback(
    async (page: number, pageSize: number) => {
      if (loadingMore || !hasMore) return
      try {
        setLoadingMore(true)
        const token = getAccessToken(store.getState().auth)
        const response = await fetchFundTransactionsFiltered(activeSorting, activeFilters, page, pageSize, token)
        dispatch(appendFundTransactions({ response }))
      } catch (e) {
        if (process.env.NODE_ENV === 'development') console.error('Error loading more fund transactions:', e)
      } finally {
        setLoadingMore(false)
      }
    },
    [dispatch, activeFilters, activeSorting, hasMore, loadingMore]
  )

  // Filter options once prefs (and therefore auth) are ready.
  useEffect(() => {
    if (!prefsReady) return
    const token = getAccessToken(store.getState().auth)
    if (!token) return
    let cancelled = false
    setFilterOptionsLoading(true)
    ;(async () => {
      const res = await fetchFundTransactionsFilterOptions(token)
      if (!cancelled) {
        setFilterOptions(res.data || {})
        setFilterOptionsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [prefsReady])

  // Refetch whenever filters/sorting change (after prefs are ready).
  useEffect(() => {
    if (!prefsReady) return
    const token = getAccessToken(store.getState().auth)
    if (token) {
      handleFetch(activeSorting, activeFilters)
    }
  }, [prefsReady, activeFilters, activeSorting, handleFetch])

  // Persist filter/sort changes (skip the first render - it hydrated from prefs).
  useEffect(() => {
    if (!prefsReady || !menuItemId) return
    if (skipPersistRef.current) {
      skipPersistRef.current = false
      return
    }
    void persistMergedPreference().catch((err) => {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to persist fund transactions table preferences:', err)
      }
    })
  }, [prefsReady, menuItemId, activeFilters, activeSorting, persistMergedPreference])

  return {
    data,
    dataLoading,
    activeFilters,
    activeSorting,
    preferenceRow,
    hasMore,
    currentPage,
    loadingMore,
    filterOptions,
    filterOptionsLoading,
    handleFetch,
    refetch: () => handleFetch(activeSorting, activeFilters),
    handleSetFilters,
    handleSetSorting,
    handleResetFilters,
    handleLoadMore,
    defaultTableFilters: DEFAULT_FUND_TRANSACTIONS_FILTERS,
    defaultTableSorting,
    persistColumnFieldOrder,
    resetColumnLayoutToDefault
  }
}
